/**
 * Money + date helpers for the Ads console.
 *
 * Amounts travel as integer MINOR units (`spendMinor`, `dailyCapMinor`, …) in
 * the connection's currency. "Minor" is the ISO 4217 exponent from the fixed
 * table in @pontimed/types — the same one the server-side ads sync stores with
 * (2 for EUR/RON/BGN/USD/HUF, 0 for JPY). Not the browser's Intl data: newer
 * ICU says 0 digits for HUF, which displayed fillér as forints. The same
 * exponent displays a number and turns a typed cap back into minor units.
 */

import { currencyExponent } from "@pontimed/types";

const ISO_CURRENCY = /^[A-Z]{3}$/;

/** Fraction digits of `currency` per ISO 4217; 2 when unknown. */
export function currencyDigits(currency: string | null | undefined): number {
  return currencyExponent(currency);
}

/** `12345, "EUR"` → "€123.45" (locale-aware). `null` → "—". */
export function formatMinor(
  minor: number | null | undefined,
  currency: string | null | undefined,
  locale: string,
): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) return "—";
  const digits = currencyDigits(currency);
  const major = minor / 10 ** digits;
  if (currency && ISO_CURRENCY.test(currency)) {
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(major);
    } catch {
      // fall through to the plain number
    }
  }
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(major);
}

/** Integer count, locale-grouped. `null` → "—". */
export function formatCount(value: number | null | undefined, locale: string): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(locale).format(value);
}

/** Minor units → the plain string an input shows ("150.5" → "150.50"). Empty when null. */
export function minorToInput(minor: number | null, currency: string | null | undefined): string {
  if (minor === null || !Number.isFinite(minor)) return "";
  const digits = currencyDigits(currency);
  return (minor / 10 ** digits).toFixed(digits);
}

export type ParsedAmount = { ok: true; minor: number | null } | { ok: false };

/**
 * A typed major-unit amount → minor units. Empty = no cap (`null`). Accepts a
 * comma or a dot as the decimal separator, rejects more fraction digits than
 * the currency has, negatives, zero and anything that is not a plain number.
 */
export function parseMajorToMinor(
  input: string,
  currency: string | null | undefined,
): ParsedAmount {
  const trimmed = input.trim().replace(/\s/g, "");
  if (trimmed === "") return { ok: true, minor: null };
  const normalised = trimmed.replace(",", ".");
  const digits = currencyDigits(currency);
  const pattern = digits > 0 ? new RegExp(`^\\d+(\\.\\d{0,${digits}})?$`) : /^\d+$/;
  if (!pattern.test(normalised)) return { ok: false };
  const minor = Math.round(Number(normalised) * 10 ** digits);
  if (!Number.isSafeInteger(minor) || minor <= 0) return { ok: false };
  return { ok: true, minor };
}

/** Local calendar date as `YYYY-MM-DD` (not UTC — "today" is the admin's today). */
export function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Inclusive range ending today: `days = 7` → the last seven calendar days. */
export function rangeForDays(days: number, today: Date = new Date()): { from: string; to: string } {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (days - 1));
  return { from: toIsoDate(start), to: toIsoDate(today) };
}

/** `YYYY-MM-DD` → short locale date, parsed as a LOCAL date (no UTC shift). */
export function formatIsoDay(value: string, locale: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return value;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** ISO timestamp → locale date + time, or "—". */
export function formatDateTime(value: string | null | undefined, locale: string): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
}
